import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'
import { FaCheckSolid } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type Props = {
  /** 現在の表示 step (1 = ファイル選択、 2 = プレビュー)。 */
  currentStep: 1 | 2
  /** step 1 / 2 の表示ラベル。 */
  stepLabels: [string, string]
}

/**
 * 2 ステップウィザードのステップインジケータ
 * (design `admin/uploads/*.html` `#up-stepper` 相当)。 CSV アップロード
 * ウィザード各画面で共用するためラベルは props で受ける。
 *
 * `WorkflowCaseDetail/components/WizardStepper` (丸数字 + タイトル + connector)
 * と同じ構造パターンを、 2 ステップ固定の簡易版として踏襲する。
 */
export const Stepper = ({
  currentStep,
  stepLabels,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const steps: Array<{ step: 1 | 2; label: string }> = [
    { step: 1, label: stepLabels[0] },
    { step: 2, label: stepLabels[1] },
  ]

  return (
    // `aria-label` は role="list" 要素に accessible name を与えるためのもの
    // (支援技術に「何のリストか」を伝える)。
    <HStack
      align="center"
      justify="center"
      gap={4}
      padding="12px 16px"
      background="Surface.primary"
      // design `.wizard-steps.up-stepper-card`: 下のパネル (`.up-panel`) と同じ淡い枠と角丸
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      aria-label={t('common:csvUpload.stepperAriaLabel')}
      role="list"
    >
      {steps.map(({ step, label }, idx) => {
        const done = step < currentStep
        const active = step === currentStep
        const isLast = idx === steps.length - 1

        return (
          <HStack
            key={step}
            align="center"
            gap={4}
            // `aria-current` は現在地を示す ARIA 標準トークンで支援技術が読む
            // (`SubMenuItems` の `aria-current="page"` と同じ公開 state
            // パターン)。 `done` (完了) には対応する ARIA state が無いため、
            // `data-state` (test/style 用のカスタム属性。 `ApprovalWizard` の
            // 同名パターンを踏襲) で表す — 両状態を同じ属性に混ぜない。
            aria-current={active ? 'step' : undefined}
            data-state={done ? 'done' : active ? 'active' : 'pending'}
            role="listitem"
          >
            <HStack align="center" gap={2}>
              <Box
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                display="inline-flex"
                // design `.wizard-step-num` = 40px の円
                boxSize="40px"
                color={done ? 'Text.highEmphasisInverse' : 'Text.midEmphasis'}
                background={done ? 'Surface.accentPrimary' : 'Surface.primary'}
                borderWidth="2px"
                borderColor={
                  done || active ? 'Border.accentPrimary' : 'Border.midEmphasis'
                }
                borderRadius="full"
              >
                {done ? (
                  <FaCheckSolid boxSize={iconSize.stepperDoneMark} />
                ) : (
                  <Text as="span" textStyle="sectionTitle">
                    {step}
                  </Text>
                )}
              </Box>
              {/* ラベルは全 step 高強調。 design `.wizard-step-label` は state で色を
                  変えない (UI 辞書 components/wizard-stepper.md)。 current の区別は
                  円の border のみ。 */}
              <Text textStyle="sectionTitle" color="Text.highEmphasis">
                {label}
              </Text>
            </HStack>
            {!isLast && (
              <Box
                flexShrink={0}
                width="48px"
                height="2px"
                background={
                  done ? 'Surface.accentPrimary' : 'Border.lowEmphasis'
                }
              />
            )}
          </HStack>
        )
      })}
    </HStack>
  )
}
